/* eslint-disable react-refresh/only-export-components */
import { useMemo } from "react";
import { client, ClusterSummaryDto } from "@/client";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useTimeSettings } from "@/state/time-settings";
import { usePermissions } from "@/hooks/use-permissions";
import useBreakpoint from "@/hooks/use-breakpoint";
import { useTooltipProps } from "@/components/custom/tooltip";
import { LinkDiv } from "@/components/custom/link-div";
import { FloatingMenu } from "@/components/custom/floating-menu";
import { selectColumn } from "@/components/custom/data-table/data-table-select-column";
import { useTableDeleteRefresh } from "@/components/custom/data-table/data-table-context";
import { useMutationWithCache } from "@/hooks/use-mutation-state";

import { showActionErrorToast } from "@/state/error-toast";

function ClusterDeleteMenu({ id }: { id: number }) {
  const { permissions } = usePermissions();
  const deleteRefresh = useTableDeleteRefresh();
  const deleteCluster = useMutationWithCache({
    ...client.clusters.delete.assign({ id }).asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: () => deleteRefresh(id),
  });

  const menuRows = [
    permissions.clusters.delete
      ? {
          text: "Remove",
          onClick: () => deleteCluster.mutateAsync({}),
        }
      : { text: "No permissions to remove cluster" },
  ];
  return <FloatingMenu menuRows={menuRows} />;
}

export const useColumns = (): ColumnDef<ClusterSummaryDto, any>[] => {
  const { t } = useTranslation();
  const timeSettings = useTimeSettings();
  const { permissions } = usePermissions();
  const isMobile = !useBreakpoint("md");
  const tooltipProps = useTooltipProps();

  return useMemo(() => {
    const columns: (ColumnDef<ClusterSummaryDto, any> | boolean)[] = [
      permissions.clusters.edit && (selectColumn as any),
      {
        id: "name",
        header: t("name"),
        accessorKey: "name",
        size: isMobile ? 200 : 50,
        meta: {
          // width: isMobile ? "2fr" : "0.5fr",
        },
        cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <div {...tooltipProps} data-tooltip-content={cell.getValue()}>
              {value}
            </div>
          );
        },
      },
      {
        id: "ready",
        header: t("ready"),
        accessorFn: (row) => row.readyDevices.length,
        size: isMobile ? 100 : 30,
        meta: {
          width: isMobile ? "1fr" : "0.5fr",
        },
        cell: ({ cell }) => {
          const value = cell.getValue();
          return <span>{value}</span>;
        },
      },
      !isMobile && {
        id: "offline",
        header: t("offline"),
        accessorFn: (row) => row.offlineDevices.length,
        size: isMobile ? 100 : 30,
        meta: {
          width: isMobile ? "1fr" : "0.5fr",
        },
        cell: ({ cell }) => {
          const value = cell.getValue();
          return <span>{value}</span>;
        },
      },
      !isMobile && {
        id: "total",
        header: t("total"),
        accessorFn: (row) =>
          row.offlineDevices.length + row.readyDevices.length,
        size: isMobile ? 100 : 30,
        meta: {
          width: isMobile ? "1fr" : "0.5fr",
        },
        cell: ({ cell }) => {
          const value = cell.getValue();
          return <span>{value}</span>;
        },
      },
      {
        header: t("collaborator"),
        size: isMobile ? 100 : 20,
        meta: {
          disablePreview: true,
          width: isMobile ? "1fr" : "96px",
        },
        cell: ({ row }) => {
          if (
            row?.original?.collaborator?.id &&
            row?.original?.collaborator?.name
          ) {
            return (
              <LinkDiv
                link={
                  permissions.devices.view &&
                  row.original.collaborator.id && {
                    to: `/dashboard/resources/devices/${row.original.collaborator.id}/`,
                    search: { __clear: true, clusterId: row.original.id },
                  }
                }
              >
                {row.original.collaborator.name}
              </LinkDiv>
            );
          } else {
            return "";
          }
        },
      },
      {
        header: t("deployedModel"),
        accessorKey: "clusterModelDeployment",
        meta: {
          disablePreview: true,
        },
        cell: ({ cell }) => {
          const value = cell.getValue();
          if (value?.model) {
            return (
              // <span>{value.model.name}</span>
              <LinkDiv
                link={
                  permissions.models.view && {
                    to: `/dashboard/models/${value.model.id}`,
                    search: { __clear: true },
                  }
                }
              >
                {value.model.name}
              </LinkDiv>
            );
          } else {
            return "";
          }
        },
      },
      {
        header: t("devicesModels"),
        accessorKey: "models",
        cell: ({ cell }) => {
          const models = cell.getValue();
          return (
            <div>
              {models.map((model) => (
                <div key={model.name}>{model.name}</div>
              ))}
            </div>
          );
        },
      },
      !isMobile &&
        permissions.clusters.delete && {
          id: "floating-menu",
          header: "",
          accessorFn: (row) => row.name,
          meta: {
            disablePreview: true,
            width: "48px",
            minWidth: "48px",
            maxWidth: "48px",
          },
          size: 48,
          minSize: 48,
          maxSize: 48,
          cell: ({ cell }) => <ClusterDeleteMenu id={cell.row.original.id} />,
        },
    ];
    return columns.filter(Boolean) as any;
  }, [t, timeSettings, isMobile, tooltipProps, permissions]);
};
